import { useState, type ReactNode } from 'react';
import { Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useReportProfile, useReportVideo } from '@/hooks/useReports';
import {
  REPORT_TYPES,
  REPORT_TYPE_DESCRIPTIONS,
  REPORT_TYPE_LABELS,
  type ReportType,
} from '@/lib/reportUtils';
import type { VideoData } from '@/lib/videoTypes';

interface ReportDialogProps {
  video?: VideoData;
  profilePubkey?: string;
  trigger?: ReactNode;
}

/** NIP-56 (kind 1984) report dialog for videos and profiles. */
export function ReportDialog({ video, profilePubkey, trigger }: ReportDialogProps) {
  const { user } = useCurrentUser();
  const reportVideo = useReportVideo();
  const reportProfile = useReportProfile();

  const [open, setOpen] = useState(false);
  const [type, setType] = useState<ReportType>('other');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const target = video ? `video "${video.title}"` : 'this profile';

  const submit = async () => {
    if (!user) return;
    setSubmitting(true);
    try {
      if (video) {
        await reportVideo(video, type, reason);
      } else if (profilePubkey) {
        await reportProfile(profilePubkey, type, reason);
      }
      setOpen(false);
      setReason('');
      setType('other');
    } catch {
      // toast handled in hooks
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button variant="ghost" size="sm" className="gap-1.5">
            <Flag className="h-4 w-4" /> Report
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Report {video ? 'video' : 'profile'}</DialogTitle>
          <DialogDescription>
            Report {target} with a NIP-56 (kind 1984) report event.
          </DialogDescription>
        </DialogHeader>

        {!user ? (
          <p className="text-sm text-muted-foreground">You must be logged in to report content.</p>
        ) : (
          <div className="space-y-4">
            <RadioGroup value={type} onValueChange={(v) => setType(v as ReportType)} className="space-y-2">
              {REPORT_TYPES.map((t) => (
                <div key={t} className="flex items-start gap-2.5">
                  <RadioGroupItem value={t} id={`report-${t}`} className="mt-0.5" />
                  <Label htmlFor={`report-${t}`} className="flex flex-col gap-0.5 cursor-pointer font-normal">
                    <span className="font-medium">{REPORT_TYPE_LABELS[t]}</span>
                    <span className="text-xs text-muted-foreground">{REPORT_TYPE_DESCRIPTIONS[t]}</span>
                  </Label>
                </div>
              ))}
            </RadioGroup>

            <div className="space-y-1.5">
              <Label htmlFor="report-reason">Additional details (optional)</Label>
              <Textarea
                id="report-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="Add context for relay operators and other users…"
              />
            </div>

            <Button onClick={submit} disabled={submitting} variant="destructive" className="w-full">
              {submitting ? 'Submitting…' : 'Submit report'}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
